import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import flow from 'lodash/flow';
import merge from 'lodash/merge';
import get from 'lodash/get';

import { denormalize } from 'normalizr';

import { EntitySchema, ObjectSchema } from 'lib/schema';
import normalizeApiResponse from 'utils/normalizeApiResponse';
import walkNestedData from 'common/utils/forms/walkNestedData';

import {
    removeUnreferencedLogicRules,
    replaceFieldNodeIdsWithNewUUIDs,
    replaceFormDataIdsWithNewUUIDs,
    replaceFormLogicIdsWithNewUUIDs,
    transformDateRange,
    transformInputData,
    transformInputPayloadFormData,
    transformOutputPayloadFormData,
    transformResponseLimitValuesIntoLogicRules,
    transformStyleConfiguration,
} from './utils';

import { formRootNodeSchema } from '../formNode';

// Constants
export const ENTITIES_KEY_FORM = 'forms';
export const ENTITIES_KEY_FORM_TEMPLATE = 'formTemplates';

export const FORM_TYPE_RSVP = 'RSVP';
export const FORM_TYPE_TICKET_SELECTION = 'ticketSelection';
export const FORM_TYPE_TICKET_SALE = 'ticketSale';

export const ADD_FORM_DATA = 'entities/ADD_FORM_DATA';
export const UPDATE_FORM = 'entities/UPDATE_FORM';
export const SET_ERROR_MESSAGE = 'entities/SET_ERROR_MESSAGE';

// Defaults
export const FORM_DEFAULTS = {
    id: '',
    name: '',
    type: '',
    label: '',
    description: '',
    root: null,
    styleConfiguration: {},
    submitButtonLabel: '',
    errorMessages: {},
    formLibraryForm: null,
};

export const FORM_KEYS = [...Object.keys(FORM_DEFAULTS), 'version', 'cached', 'sessions'];

export const SERIALIZE_FORM_KEYS = FORM_KEYS;

// Processors
const processFormEntity = (form) => ({
    ...cloneDeep(FORM_DEFAULTS),
    ...pick(form, FORM_KEYS),
});

// Schemas
export const formSchema = new EntitySchema(
    ENTITIES_KEY_FORM,
    {},
    {
        processStrategy: processFormEntity,
        unprocessStrategy: SERIALIZE_FORM_KEYS,
    },
);

formSchema.define({
    root: formRootNodeSchema,
});

export const formRespSchema = new ObjectSchema({ form: formSchema });

export const formTemplateSchema = new EntitySchema(ENTITIES_KEY_FORM_TEMPLATE);
export const formTemplatesSchema = [formTemplateSchema];

// Normalizers

/**
 * Given serialized data provided by the API, it tranforms and normalizes it to produce the
 * data structure that the redux store should receive.
 *
 * @param  {Object} serializedData
 * @return {Object}
 */
export function normalizeForm(serializedData) {
    return flow(
        () => transformInputPayloadFormData(serializedData),
        transformStyleConfiguration,
        (transformedData) => normalizeApiResponse(transformedData, formSchema),
        (normalizedData) => transformInputData(normalizedData),
    )();
}

/**
 * Applies necessary transformations and serializes form entities to produce the tree structure
 * that the API accepts.
 *
 * @param  {string} formId
 * @param  {Object[]} storeEntities
 * @param  {Object} bootstrap
 * @return {Object}
 */
export function serializeForm(formId, storeEntities, bootstrap) {
    return flow(
        transformDateRange,
        transformResponseLimitValuesIntoLogicRules,
        removeUnreferencedLogicRules,
        (transformedEntities) => denormalize(formId, formSchema, transformedEntities),
        (denormalizedData) => transformOutputPayloadFormData(denormalizedData, bootstrap),
    )(storeEntities);
}

/**
 * Given normalized data, it transforms and replaces all the ids with new UUIDs
 * except the formId, rootId
 *
 * @param  {Object} normalizedData
 * @param  {String} formId
 * @param  {String} rootId
 * @return {Object}
 */
export function replaceFormNodeIdsWithUuids(normalizedData, formId, rootId) {
    // set correctId
    const formDataWithCorrectId = {
        ...normalizedData,
        id: formId,
    };

    // this will be a dictionary storing the relation between the old ids
    // and the new uuids
    let ids = {};

    const formDataWithCorrectQuestionNodeIds = {
        ...formDataWithCorrectId,
        root: walkNestedData(formDataWithCorrectId.root || {}, (formNode) => {
            const { result, newIds } = replaceFormDataIdsWithNewUUIDs(formNode, rootId);
            ids = merge(ids, newIds);
            return result;
        }),
    };

    const formDataRootLogic = get(formDataWithCorrectQuestionNodeIds, 'root.logic', []).filter(Boolean);
    const formDataRootChildren = get(formDataWithCorrectQuestionNodeIds, 'root.children', []).filter(Boolean);

    return {
        ...formDataWithCorrectQuestionNodeIds,
        root: {
            ...formDataWithCorrectQuestionNodeIds.root,
            children: replaceFieldNodeIdsWithNewUUIDs(formDataRootChildren, ids),
            logic: replaceFormLogicIdsWithNewUUIDs(formDataRootLogic, ids),
        },
    };
}

// Actions
export const addFormData = (formData) => ({
    type: ADD_FORM_DATA,
    payload: formData,
});

export const setErrorMessages = (formId, errorMessages) => ({
    type: SET_ERROR_MESSAGE,
    payload: {
        entities: {
            [ENTITIES_KEY_FORM]: {
                [formId]: {
                    errorMessages,
                },
            },
        },
    },
});

export const addRawFormData = (formData) => (dispatch) => {
    const normalizedFormData = normalizeForm(formData);
    dispatch(addFormData(normalizedFormData));
};

/**
 * @param {string} formId
 * @param {string} label
 * @return {Object}
 */
export const setFormLabel = (formId, label) => ({
    type: UPDATE_FORM,
    payload: {
        entities: {
            [ENTITIES_KEY_FORM]: {
                [formId]: {
                    label,
                },
            },
        },
    },
});

/**
 * @param {string} formId
 * @param {string} submitButtonLabel
 * @return {Object}
 */
export const setSubmitButtonLabel = (formId, submitButtonLabel) => ({
    type: UPDATE_FORM,
    payload: {
        entities: {
            [ENTITIES_KEY_FORM]: {
                [formId]: {
                    submitButtonLabel,
                },
            },
        },
    },
});

export const setTicketSelectionFormLimits = (formId, ticketSelectionLimits) => ({
    type: UPDATE_FORM,
    payload: {
        entities: {
            [ENTITIES_KEY_FORM]: {
                [formId]: {
                    limitOneOrderPerEmail: ticketSelectionLimits.limitOneOrderPerEmail,
                    limitOneTicketTypePerEmail: ticketSelectionLimits.limitOneTicketTypePerEmail,
                },
            },
        },
    },
});

export const setFormLibraryName = (formId, newName) => ({
    type: UPDATE_FORM,
    payload: {
        entities: {
            [ENTITIES_KEY_FORM]: {
                [formId]: {
                    formLibraryForm: {
                        name: newName,
                    },
                },
            },
        },
    },
});
